/**
 * Turns Judge.me's review list into a carousel: one row of review cards
 * (three on desktop, two on tablets, one on phones, set in CSS) with previous
 * and next buttons. Bill, 9/18 and 9/23: reviews took a quarter of the page;
 * show three at a time and let people arrow through the rest.
 *
 * Judge.me still owns the reviews. It loads five per page from its own API, so
 * at the end of a page the next button presses Judge.me's own (visually
 * hidden) next-page link, and the new page starts at its first card; at the
 * start of a page the previous button loads the previous page and starts at
 * its last card. Every review stays reachable, and sorting, photos and Write
 * a review are untouched.
 */
class LpReviewsCarousel extends HTMLElement {
  connectedCallback() {
    this.prevButton = this.querySelector('[data-lp-rev-prev]');
    this.nextButton = this.querySelector('[data-lp-rev-next]');
    this.status = this.querySelector('[data-lp-rev-status]');
    if (!this.prevButton || !this.nextButton) return;

    this.prevButton.addEventListener('click', () => this.#step(-1));
    this.nextButton.addEventListener('click', () => this.#step(1));

    // Judge.me hydrates the widget and swaps the list on every page change.
    // Watch the widget only: the status line below it is ours, and observing
    // it would feed every status update straight back into this callback.
    this.widget = this.querySelector('.jdgm-widget');
    if (!this.widget) return;
    this.observer = new MutationObserver(() => this.#onListChange());
    this.observer.observe(this.widget, { childList: true, subtree: true });
    this.#onListChange();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
  }

  get #track() {
    return this.querySelector('.jdgm-rev-widg__reviews');
  }

  #pageLink(kind) {
    return this.querySelector(`.jdgm-paginate__${kind}-page`);
  }

  #atStart(track) {
    return track.scrollLeft <= 4;
  }

  #atEnd(track) {
    return track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
  }

  #step(direction) {
    const track = this.#track;
    if (!track) return;
    const edge = direction > 0 ? this.#atEnd(track) : this.#atStart(track);

    if (!edge) {
      track.scrollBy({ left: direction * track.clientWidth, behavior: this.#behavior() });
      return;
    }

    const link = this.#pageLink(direction > 0 ? 'next' : 'prev');
    if (!link) return;
    this.pendingEdge = direction > 0 ? 'start' : 'end';
    this.returnFocusTo = direction > 0 ? this.nextButton : this.prevButton;
    this.classList.add('is-loading');
    this.#holdPage();
    link.click();
  }

  /**
   * On a page change Judge.me scrolls the page to its widget and focuses the
   * first new review, which moved the page about 550px and left the shopper's
   * cursor over the products below. Keep the page where it is until the new
   * reviews have landed and Judge.me's scroll animation is over.
   */
  #holdPage() {
    const scroller = this.#scroller();
    const top = scroller.scrollTop;
    const until = performance.now() + 1600;
    const hold = () => {
      window.jQuery?.('html, body').stop?.(true);
      if (Math.abs(scroller.scrollTop - top) > 1) scroller.scrollTop = top;
      if (performance.now() < until) requestAnimationFrame(hold);
    };
    requestAnimationFrame(hold);
  }

  #scroller() {
    for (let el = this.parentElement; el; el = el.parentElement) {
      const overflowY = getComputedStyle(el).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight) return el;
    }
    return document.scrollingElement || document.documentElement;
  }

  #behavior() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';
  }

  #onListChange() {
    const track = this.#track;
    if (!track) return;

    // Keep the arrows in Judge.me's sort row, above the cards, so their place
    // on screen does not move with the height of each page's reviews.
    const row = this.querySelector('.jdgm-row-actions');
    const nav = this.querySelector('.lp-rev-nav');
    if (row && nav && nav.parentElement !== row) row.append(nav);

    if (track !== this.boundTrack) {
      this.boundTrack = track;
      track.addEventListener('scroll', () => this.#update(), { passive: true });
      if (track.getAttribute('tabindex') !== '0') track.setAttribute('tabindex', '0');
      if (!track.hasAttribute('aria-label')) track.setAttribute('aria-label', 'Customer reviews');
    }

    if (this.pendingEdge && track.querySelector('.jdgm-rev')) {
      track.scrollLeft = this.pendingEdge === 'start' ? 0 : track.scrollWidth;
      this.pendingEdge = null;
      this.classList.remove('is-loading');
      // Judge.me moves focus to the first new review; hand it back to the
      // arrow so repeated presses keep working, without scrolling to it.
      requestAnimationFrame(() => this.returnFocusTo?.focus({ preventScroll: true }));
    }

    this.#update();
  }

  #update() {
    const track = this.#track;
    if (!track) return;
    this.prevButton.disabled = this.#atStart(track) && !this.#pageLink('prev');
    this.nextButton.disabled = this.#atEnd(track) && !this.#pageLink('next');

    const current = this.querySelector('.jdgm-paginate__page.jdgm-curt');
    const last = this.querySelector('.jdgm-paginate__last-page') || [...this.querySelectorAll('.jdgm-paginate__page[data-page]')].pop();
    if (this.status && current && last) {
      const text = `Page ${current.dataset.page} of ${Math.max(Number(last.dataset.page), Number(current.dataset.page))}`;
      if (this.status.textContent !== text) this.status.textContent = text;
    }
  }
}

if (!customElements.get('lp-reviews-carousel')) customElements.define('lp-reviews-carousel', LpReviewsCarousel);
